import { Combobox } from "@dashboard/components/Combobox";
import { useWsmFitmentAxisValuesQuery } from "@dashboard/graphql";
import { FetchMoreProps } from "@dashboard/types";
import { Option } from "@saleor/macaw-ui-next";
import React from "react";

import { WsmFitmentValuePick } from "../types";
import { hasExactOption, sortValueOptions } from "../utils";

/** The API caps a page at 100. */
const OPTIONS_PAGE = 100;

export interface WsmFitmentPickerProps {
  label: string;
  options: Option[];
  /** The text the options were fetched (or filtered) for; decides "Add new value". */
  search: string;
  /** Debounced by the Combobox; the caller filters by it. */
  fetchOptions: (search: string) => void;
  fetchMore?: FetchMoreProps;
  loading?: boolean;
  disabled?: boolean;
  error?: string;
  value: WsmFitmentValuePick | undefined;
  onChange: (pick: WsmFitmentValuePick | undefined) => void;
  "data-test-id"?: string;
}

/**
 * One value from a list, or text the merchant typed. An option's value is the
 * row id; a custom value is the text itself, told apart by not being an option.
 * "Add new value" is offered only while no option already spells the text
 * (ignoring case): the bulk create would hand that row back anyway.
 */
export const WsmFitmentPicker = ({
  label,
  options,
  search,
  fetchOptions,
  fetchMore,
  loading,
  disabled,
  error,
  value,
  onChange,
  ...rest
}: WsmFitmentPickerProps) => (
  <Combobox
    label={label}
    options={options}
    loading={loading}
    disabled={disabled}
    fetchOptions={fetchOptions}
    fetchMore={fetchMore}
    allowCustomValues={!hasExactOption(options, search)}
    allowEmptyValue
    value={value ? { value: value.id ?? value.text, label: value.text } : null}
    onChange={event => {
      const picked = event.target.value as string | null;

      if (!picked) {
        onChange(undefined);

        return;
      }

      const option = options.find(candidate => candidate.value === picked);

      onChange(option ? { id: option.value, text: option.label } : { text: picked });
    }}
    error={!!error}
    helperText={error}
    {...rest}
  />
);

export interface WsmFitmentAxisValuePickerProps
  extends Omit<
    WsmFitmentPickerProps,
    "options" | "loading" | "search" | "fetchOptions" | "fetchMore"
  > {
  axisId: string;
}

/**
 * The picker for one axis: its own query, so the dialog can hold one per axis.
 * The typed text goes to the server as `search` (the Combobox debounces it);
 * the list pages on scroll with the same search.
 */
export const WsmFitmentAxisValuePicker = ({ axisId, ...rest }: WsmFitmentAxisValuePickerProps) => {
  const [search, setSearch] = React.useState("");
  const { data, loading, fetchMore } = useWsmFitmentAxisValuesQuery({
    variables: { axis: axisId, first: OPTIONS_PAGE, search: search.trim() || null },
    notifyOnNetworkStatusChange: true,
  });
  // The API pages in (updatedAt, id) order; sorted here, numbers by value, so a
  // Year axis reads in order. This sorts the set loaded so far (the search's
  // pages fetched on scroll), not the whole axis: a page fetched later can
  // slot rows in above ones already shown. Typing to search narrows it anyway.
  const options = React.useMemo<Option[]>(
    () =>
      sortValueOptions(
        data?.wsmFitmentAxisValues?.edges.map(edge => ({
          value: edge.node.id,
          label: edge.node.value,
        })) ?? [],
      ),
    [data],
  );
  const pageInfo = data?.wsmFitmentAxisValues?.pageInfo;
  const onFetchMore = () => {
    // A scroll can fire again before the page lands; one request per cursor.
    if (loading || !pageInfo?.hasNextPage) {
      return;
    }

    fetchMore({
      variables: { after: pageInfo?.endCursor },
      updateQuery: (previous, { fetchMoreResult }) => {
        if (!fetchMoreResult?.wsmFitmentAxisValues) {
          return previous;
        }

        return {
          ...fetchMoreResult,
          wsmFitmentAxisValues: {
            ...fetchMoreResult.wsmFitmentAxisValues,
            edges: [
              ...(previous.wsmFitmentAxisValues?.edges ?? []),
              ...fetchMoreResult.wsmFitmentAxisValues.edges,
            ],
          },
        };
      },
    });
  };

  return (
    <WsmFitmentPicker
      options={options}
      // Until the answer for this text is in, the old page cannot rule a match out.
      search={loading ? "" : search}
      fetchOptions={setSearch}
      fetchMore={{ hasMore: !!pageInfo?.hasNextPage, loading, onFetchMore }}
      loading={loading}
      {...rest}
    />
  );
};

export interface WsmFitmentListPickerProps
  extends Omit<WsmFitmentPickerProps, "options" | "search" | "fetchOptions" | "fetchMore"> {
  /** The whole list, already loaded (a qualifier's values ride on the qualifier). */
  values: Array<{ id: string; value: string }>;
}

/** A picker over a list already in hand: filtered here, no server search. */
export const WsmFitmentListPicker = ({ values, ...rest }: WsmFitmentListPickerProps) => {
  const [search, setSearch] = React.useState("");
  const needle = search.trim().toLowerCase();
  // Loaded whole, so this sort is the list's real order (numbers by value).
  const options = sortValueOptions(
    values
      .filter(value => !needle || value.value.toLowerCase().includes(needle))
      .map(value => ({ value: value.id, label: value.value })),
  );

  return <WsmFitmentPicker options={options} search={search} fetchOptions={setSearch} {...rest} />;
};

export default WsmFitmentAxisValuePicker;
